import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

import { IoService } from '@core/services/io.service';
import { LoggingService, LogSeverity } from '@core/services/logging.service';
import { VersionService, VersionInfo } from '@core/services/version.service';

import {
  LtoPricingRequest,
  LtoPricingResponse,
  LtoPricingFormatted,
  LtoPricingRequestCombo,
  LtoPricingRequestItem,
} from '@core/models/lto-pricing.model';

import { Campaign, CampaignBanner, CampaignBannerItem, CampaignContent, CampaignDaypart } from '@core/models';
import { LaunchDarklyService } from '@core/services/launchdarkly.service';

@Injectable({ providedIn: 'root' })
export class LtoPricingService implements OnDestroy {
  private readonly CACHE_KEY = 'ltoPricingCache';
  private readonly CACHE_EXPIRATION_MINUTES = 60;

  /* ------------------------------------------------------------------ */
  /*  Build version captured once, never "unknown" afterwards           */
  /* ------------------------------------------------------------------ */
  private buildVersion = 'unknown'; // ⬅︎ gets overwritten in waitForVersion()

  private get appVersion(): string {
    //  kept for readability
    return this.buildVersion;
  }

  /* ------------------------------------------------------------------ */
  /*  Reactive pricing stream                                           */
  /* ------------------------------------------------------------------ */
  private pricingDataSubject = new BehaviorSubject<LtoPricingFormatted | null>(
    null, // seed later
  );
  readonly pricingData$ = this.pricingDataSubject.asObservable();

  private destroyed$ = new Subject();

  constructor(
    private ioService: IoService,
    private logger: LoggingService,
    private versionService: VersionService,
    private ldService: LaunchDarklyService,
  ) {}

  // When the service is destroyed, clean up any subscriptions that need tobe
  ngOnDestroy(): void {
    this.destroyed$.next(undefined);
    this.destroyed$.complete();
  }

  /* ================================================================== */
  /*  Public API                                                        */
  /* ================================================================== */

  requestLtoPricing(): void {
    this.waitForVersion().then(() => this.startPricingFlow());
  }

  refreshVersion(): void {
    this.versionService.refresh().pipe(takeUntil(this.destroyed$)).subscribe();
  }

  /* ================================================================== */
  /*  Internals                                                         */
  /* ================================================================== */
  private isCombo(value: unknown): value is LtoPricingRequestCombo {
    return (
      typeof value === 'object' &&
      value !== null &&
      'promoId' in value &&
      'mainPosItemId' in value &&
      'sidePosItemId' in value &&
      'drinkPosItemId' in value
    );
  }

  private buildLtoPricingPayload(campaigns: Campaign[]): LtoPricingRequest {
    const items: LtoPricingRequest['items'] = [];
    const combos: LtoPricingRequest['combos'] = [];
    const seenItems = new Set<string>();
    const seenCombos = new Set<string>();

    if (!Array.isArray(campaigns)) {
      if (typeof campaigns === 'object' && campaigns !== null && Array.isArray((campaigns as any).campaigns)) {
        campaigns = (campaigns as any).campaigns;
      } else if (typeof campaigns === 'object' && campaigns !== null && Array.isArray((campaigns as any).data)) {
        campaigns = (campaigns as any).data;
      } else {
        return { items, combos };
      }
    }

    campaigns.forEach((campaign: Campaign) => {
      if (!campaign || !Array.isArray(campaign.dayparts)) return;
      campaign.dayparts.forEach((daypart: CampaignDaypart) => {
        if (!daypart || !Array.isArray(daypart.content)) return;
        daypart.content.forEach((content: CampaignContent) => {
          if (!content || !Array.isArray(content.banners)) return;
          content.banners.forEach((banner: CampaignBanner) => {
            if (!banner || !Array.isArray(banner.items)) return;
            banner.items.forEach((item: CampaignBannerItem) => {
              if (!item || !item.pos) return;
              if (Object.values(item.pos).length === 1) {
                const posItem = item.pos as LtoPricingRequestItem;
                const key = posItem.posItemId;
                if (key && !seenItems.has(key)) {
                  seenItems.add(key);
                  items.push({ posItemId: posItem.posItemId });
                }
              } else if (this.isCombo(item.pos)) {
                const combo = item.pos;
                const key = this.comboKey(combo);
                if (!seenCombos.has(key)) {
                  seenCombos.add(key);
                  combos.push(combo);
                }
              }
            });
          });
        });
      });
    });
    return { items, combos };
  }

  /** Stable key from the unique combination of all ids on a combo */
  private comboKey(combo: LtoPricingRequestCombo): string {
    const side = Array.isArray(combo.sidePosItemId) ? [...combo.sidePosItemId].sort().join(',') : combo.sidePosItemId;
    return [combo.promoId, combo.mainPosItemId, side, combo.drinkPosItemId].join('|');
  }

  /** Resolves when VersionService has emitted once; caches buildVersion */
  private waitForVersion(): Promise<void> {
    return firstValueFrom(
      this.versionService.versionInfo$.pipe(
        filter((v): v is VersionInfo => !!v),
        take(1),
      ),
    ).then((v) => {
      this.buildVersion = v.currentVersion;

      /* ---------- purge stale cache immediately if build or storeId changed ---------- */
      const raw = localStorage.getItem(this.CACHE_KEY);
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          const currentStoreId = localStorage.getItem('storeId') || '';
          if (parsed.version !== this.buildVersion || parsed.storeId !== currentStoreId) {
            localStorage.removeItem(this.CACHE_KEY);
            this.logger.writeLog('[LtoPricingService] Purged cache from old build or previous store', {
              oldStore: parsed.storeId,
              newStore: currentStoreId,
              oldVersion: parsed.version,
              newVersion: this.buildVersion,
              severity: LogSeverity.DEBUG,
            });
          }
        } catch {
          localStorage.removeItem(this.CACHE_KEY);
        }
      }
    });
  }

  private startPricingFlow(): void {
    /*
    this.logger.writeLog('[LtoPricingService] Subscribing to LTO pricing updates', {
      severity: LogSeverity.INFO,
      appVersion: this.appVersion,
    });
    */

    /* listen for server push */
    this.ioService.onPriceOverlayReceived(this.handlePricingResponse.bind(this));

    /* prime with fresh cache now that buildVersion is known */
    const fresh = this.getCachedPricing(false);
    if (fresh) {
      this.logger.writeLog('[LtoPricingService] Using cached pricing data', {
        severity: LogSeverity.DEBUG,
      });
      this.pricingDataSubject.next(fresh);
    } else {
      /* fall back to expired cache */
      const expired = this.getCachedPricing(true);
      if (expired) {
        this.logger.writeLog('[LtoPricingService] Using expired cached data as fallback', {
          severity: LogSeverity.DEBUG,
        });
        this.pricingDataSubject.next(expired);
      }
    }

    // Subscribe to the menu content until the service is destroyed
    this.ldService
      .currentMenuContent()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((data: Campaign[]) => {
        const fresh = this.getCachedPricing(false);
        if (!fresh) {
          /* request fresh numbers from proxy */
          const payload = this.buildLtoPricingPayload(data);

          if (!payload.items?.length && !payload.combos?.length) {
            this.logger.writeLog('[LtoPricingService] Skipping pricing request — no items or combos', {
              severity: LogSeverity.DEBUG,
            });
            return;
          }

          this.logger.writeLog('[LtoPricingService] Requesting pricing from WebProxy', {
            payload,
            severity: LogSeverity.INFO,
          });

          this.ioService.requestPriceOverlay(payload);
        } else {
          if (JSON.stringify(this.pricingDataSubject.getValue()) !== JSON.stringify(fresh)) {
            this.pricingDataSubject.next(fresh);
          }
        }
      });
  }

  private handlePricingResponse(response: LtoPricingResponse): void {
    this.logger.writeLog('[LtoPricingService] Received pricing response', {
      response,
      severity: LogSeverity.INFO,
    });

    const transformed = this.transformLtoPrices(response);

    /* write to cache tagged with correct build version */
    localStorage.setItem(
      this.CACHE_KEY,
      JSON.stringify({
        timestamp: Date.now(),
        version: this.appVersion, // now always the real build string
        storeId: localStorage.getItem('storeId') ?? null,
        data: transformed,
      }),
    );

    if (JSON.stringify(this.pricingDataSubject.getValue()) !== JSON.stringify(transformed)) {
      this.pricingDataSubject.next(transformed);
    }
  }

  private transformLtoPrices(res: LtoPricingResponse): LtoPricingFormatted {
    return {
      items: (res.items ?? []).reduce<Record<string, number>>((acc, i) => {
        acc[i.posItemId] = i.price;
        return acc;
      }, {}),
      combos: (res.combos ?? []).reduce<Record<string, number>>((acc, c) => {
        acc[c.promoId] = c.price;
        return acc;
      }, {}),
    };
  }

  private getCachedPricing(allowExpired: boolean): LtoPricingFormatted | null {
    try {
      const raw = localStorage.getItem(this.CACHE_KEY);
      if (!raw) return null;

      const { timestamp, version, storeId, data } = JSON.parse(raw) as {
        timestamp: number;
        version: string;
        storeId: string | null;
        data: LtoPricingFormatted;
      };

      const expired = Date.now() - timestamp > this.CACHE_EXPIRATION_MINUTES * 60 * 1000;
      const matchesVersion = version === this.appVersion;
      const matchesStoreId = storeId === (localStorage.getItem('storeId') ?? null);

      this.logger.writeLog('[LtoPricingService] Cached data inspected', {
        severity: LogSeverity.DEBUG,
        details: {
          expired,
          matchesVersion,
          matchesStoreId,
          versionStored: version,
          storeIdStored: storeId,
        },
      });

      if ((!expired || allowExpired) && matchesVersion && matchesStoreId) {
        return data;
      }
    } catch {
      /* corrupted JSON – ignore */
    }
    return null;
  }

  /**
   * Function to fetch displayable content that has been authored for a particular
   * campaign and daypart.
   * @param campaignId - ID for the campaign we are looking for. ex "national"
   * @param daypartId  - ID for the daypart we are looking for. ex "breakfast"
   * @param contentId  - ID for the content section we are looking for.
   * @returns
   */
  public getCampaignContent(
    menuContent: Campaign[],
    campaignId: string,
    daypartId: string,
    contentId: string,
  ): CampaignContent | undefined {
    if (!Array.isArray(menuContent)) {
      if (typeof menuContent === 'object' && menuContent !== null && Array.isArray((menuContent as any).campaigns)) {
        menuContent = (menuContent as any).campaigns;
      } else if (typeof menuContent === 'object' && menuContent !== null && Array.isArray((menuContent as any).data)) {
        menuContent = (menuContent as any).data;
      } else {
        return undefined;
      }
    }
    const campaign = menuContent.find((campaign: Campaign) => campaign?.id === campaignId);
    const daypart = campaign?.dayparts?.find((daypart: CampaignDaypart) => daypart?.id === daypartId);
    const content = daypart?.content?.find((content: CampaignContent) => content?.id === contentId);
    return content;
  }
}
